import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { Table } from 'components/Table';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { formatCodesForDisplay, packageDxOidForHttp } from 'services/OidSupport';

export const DiagnosisDetails = () => {
    const { diagnosisOid } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/diagnoses');

    const isCreate = diagnosisOid === CREATE_ROUTE;

    const diagnosis = useGet(!isCreate ? `/reference/diagnoses/${diagnosisOid}` : null);

    const [showWarning, setShowWarning] = useState(false);

    return (
        <Formik
            initialValues={{
                oidName: diagnosis?.oid || '',
                description: diagnosis?.description || '',
                codes: formatCodesForDisplay(diagnosis?.codes ?? []),
            }}
            enableReinitialize
            validationSchema={Yup.object({
                oidName: Yup.string()
                    .max(50, validationStrings.maxCharacter[50])
                    .required(validationStrings.requiredField.errorMessage),
                description: Yup.string()
                    .max(50, validationStrings.maxCharacter[50])
                    .required(validationStrings.requiredField.errorMessage),
                codes: Yup.string().required(validationStrings.requiredField.errorMessage),
            })}
            onSubmit={async (values) => {
                try {
                    const body = packageDxOidForHttp(
                        values.oidName,
                        values.description,
                        values.codes,
                    );

                    if (isCreate) {
                        await post('/reference/diagnoses/', body);
                    } else {
                        await put(`/reference/diagnoses/${diagnosis.oid}`, body);
                    }

                    goHome();
                } catch (err) {
                    handleError('Error saving diagnosis.', err);
                }
            }}
        >
            {(formik) => (
                <>
                    <WarningDialog
                        dialogTitle={dialogStrings.unsavedChanges.title}
                        isOpen={showWarning}
                        onSecondaryButtonClick={() => setShowWarning(false)}
                        onPrimaryButtonClick={goHome}
                        dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} Diagnosis`}
                        isLoading={!isCreate && !diagnosis}
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        onSecondaryButtonClick={() => {
                            if (formik.dirty) {
                                setShowWarning(true);
                            } else {
                                goHome();
                            }
                        }}
                        formLeftContent={
                            <>
                                <TextInputFormik
                                    label="Diagnosis Name"
                                    id="oidName"
                                    required
                                    disabled={!isCreate}
                                />
                                <TextInputFormik label="Display Name" id="description" required />
                                <TextAreaFormik
                                    label="ICD-10 Codes"
                                    id="codes"
                                    helperText="Comma delimited ICD-10 code list."
                                    required
                                    fill
                                />
                                {diagnosis?.codes && (
                                    <Table
                                        columnNames={['Code', 'Display Name']}
                                        columnWidths={[undefined, 600]}
                                        tableData={diagnosis.codes.map((code) => [
                                            code.code,
                                            code.description,
                                        ])}
                                    />
                                )}
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    );
};
